import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";

const ACTION_LABELS = {
  "session.create": { label: "Created session", color: "#000000" },
  "session.update": { label: "Edited session", color: "#000000" },
  "session.delete": { label: "Deleted session", color: "#DC4F48" },
  "session.move": { label: "Moved room", color: "#43C4E3" },
  "session.bulk_move": { label: "Bulk-moved rooms", color: "#43C4E3" },
  "session.bulk_undo": { label: "Reverted bulk move", color: "#7A828C" },
  "speaker.create": { label: "Added speaker", color: "#000000" },
  "speaker.update": { label: "Edited speaker", color: "#000000" },
  "speaker.delete": { label: "Removed speaker", color: "#DC4F48" },
  "announcement.create": { label: "Sent announcement", color: "#000000" },
  "programme.sync_from_dftb26": { label: "Pulled programme from dftb26.com", color: "#43C4E3" },
  "user.promote": { label: "Made user admin", color: "#000000" },
  "user.demote": { label: "Removed admin rights", color: "#DC4F48" },
};

function describeDetails(action, d) {
  if (!d) return "";
  switch (action) {
    case "session.move":
      return `"${d.title || ""}" - ${d.from} → ${d.to}${d.notify ? " · push sent" : ""}`;
    case "session.bulk_move":
    case "session.bulk_undo":
      return `${d.moved ?? d.reverted} session(s) ${d.from} → ${d.to}${d.date ? ` · ${d.date}` : " · all days"}`;
    case "session.create":
    case "session.update":
      return `"${d.title}" · ${d.stage} · ${d.date}`;
    case "session.delete":
      return d.title ? `"${d.title}"` : "";
    case "speaker.create":
    case "speaker.update":
    case "speaker.delete":
      return d.name ? `"${d.name}"` : "";
    case "announcement.create":
      return `"${d.title}"${d.scheduled ? " · scheduled" : ""}${d.push ? " · push" : ""}`;
    case "programme.sync_from_dftb26":
      return `${d.sessions} sessions / ${d.speakers} speakers${d.errors ? ` · ${d.errors} warning(s)` : ""}`;
    case "user.promote":
    case "user.demote":
      return d.email || "";
    default:
      return "";
  }
}

export default function AuditAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/audit-log?limit=200"); setRows(data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#7A828C]">Latest 200 programme &amp; admin mutations. Helpful for &quot;wait, who changed this?&quot;</p>
        <Button size="sm" variant="outline" onClick={load} className="rounded-sm" data-testid="audit-refresh"><ArrowsClockwise size={14} className="mr-1" />Refresh</Button>
      </div>
      {loading ? (
        <div className="text-center py-8 text-[#7A828C] text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-[#7A828C] text-sm">No mutations recorded yet.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => {
            const info = ACTION_LABELS[r.action] || { label: r.action, color: "#7A828C" };
            return (
              <div key={r.id} data-testid={`audit-row-${r.id}`} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-start gap-3">
                <span aria-hidden="true" className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: info.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-medium text-sm text-[#1c1c24]">{info.label}</span>
                    <span className="font-mono-time text-[10px] text-[#7A828C] uppercase tracking-widest">
                      {new Date(r.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="text-xs text-[#5a5a5a] mt-0.5 truncate">{describeDetails(r.action, r.details)}</div>
                  <div className="text-[10px] text-[#7A828C] mt-0.5 font-mono-time">{r.actor_email}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
